import { useLang, tx } from "../i18n";
import { Icon } from "./Icon";
import { CHECKLISTS } from "../data/checklists";

const MAX_WA_ITEMS = 40;

// Texto consolidado para WhatsApp: artículos faltantes de TODAS las checklists, agrupados
function waShoppingText(groups, totalMissing, lang) {
  const lines = [];
  for (const g of groups) {
    lines.push(`— ${tx(g.title, lang)} —`);
    for (const { item } of g.missing) lines.push(`• ${tx(item, lang)}`);
  }
  const extra = totalMissing - groups.reduce((s, g) => s + g.missing.length, 0);
  return lang === "es"
    ? `LISTA DE COMPRAS FAMILIAR — preparatepr.org\nFaltan ${totalMissing} artículos en total\n\n${lines.join("\n")}${extra > 0 ? `\n…y ${extra} más (ver la app)` : ""}\n\nCoordina las compras en familia — Puerto Rico, Preparado Siempre`
    : `FAMILY SHOPPING LIST — preparatepr.org\n${totalMissing} items missing in total\n\n${lines.join("\n")}${extra > 0 ? `\n…and ${extra} more (see the app)` : ""}\n\nCoordinate shopping as a family — Puerto Rico, Always Prepared`;
}

export function ShoppingList({ checked, onToggle }) {
  const { lang } = useLang();

  // Artículos faltantes por checklist
  const groups = CHECKLISTS.map((c) => ({
    id: c.id,
    icon: c.icon,
    title: c.title,
    missing: c.items.map((item, i) => ({ item, i })).filter(({ i }) => !(checked?.[c.id] || []).includes(i)),
  })).filter((g) => g.missing.length > 0);

  const totalMissing = groups.reduce((s, g) => s + g.missing.length, 0);
  // Cap para la URL de WhatsApp
  let budget = MAX_WA_ITEMS;
  const waGroups = groups.map((g) => {
    const take = g.missing.slice(0, Math.max(0, budget));
    budget -= take.length;
    return { ...g, missing: take };
  }).filter((g) => g.missing.length > 0);

  return (
    <section data-testid="shopping-list" className="no-print mt-8 rounded-lg border border-slate-300 bg-white p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-display text-xl sm:text-2xl font-bold text-slate-900">
          <Icon name="Backpack" className="w-6 h-6 text-[#1C2B4A]" />
          {lang === "es" ? "Lista de Compras Familiar" : "Family Shopping List"}
        </h3>
        {totalMissing > 0 && (
          <a
            data-testid="shopping-share-wa"
            href={`https://wa.me/?text=${encodeURIComponent(waShoppingText(waGroups, totalMissing, lang))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border-2 border-green-700 px-3.5 py-2 text-sm font-bold text-green-700 hover:bg-green-700 hover:text-white transition-colors duration-200 active:scale-[0.98]"
          >
            <Icon name="MessageCircle" className="w-4 h-4" /> WhatsApp
          </a>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-600">
        {lang === "es"
          ? "Todo lo que falta en tus checklists, en un solo lugar. Márcalo al comprarlo y se actualiza la checklist."
          : "Everything missing from your checklists, in one place. Check it off when bought and the checklist updates."}
      </p>

      {totalMissing === 0 ? (
        <p data-testid="shopping-empty" className="mt-4 rounded-md border border-green-700 bg-green-50 p-4 text-sm font-semibold text-green-900 flex items-center gap-2">
          <Icon name="CheckCircle2" className="w-5 h-5 text-green-700 shrink-0" />
          {lang === "es" ? "¡Nada que comprar! Todas tus checklists están completas." : "Nothing to buy! All your checklists are complete."}
        </p>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {groups.map((g) => (
            <div key={g.id} data-testid={`shopping-group-${g.id}`} className="rounded-md border border-slate-200 bg-[#F8FAFC] p-4">
              <p className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-slate-700">
                <Icon name={g.icon} className="w-4 h-4 text-amber-700" />
                {tx(g.title, lang)}
                <span className="ml-auto font-mono-pr text-xs font-bold text-slate-500">{g.missing.length}</span>
              </p>
              <ul className="mt-2 space-y-1">
                {g.missing.map(({ item, i }) => (
                  <li key={i}>
                    <label data-testid={`shopping-item-${g.id}-${i}`} className="flex cursor-pointer items-start gap-2.5 py-1 group">
                      <input type="checkbox" checked={false} onChange={() => onToggle(g.id, i)} className="peer sr-only" />
                      <span className="mt-0.5 shrink-0 rounded border-2 border-slate-400 bg-white group-hover:border-green-700 transition-colors duration-150" style={{ width: "1.125rem", height: "1.125rem" }} />
                      <span className="text-sm leading-snug text-slate-700 group-hover:text-slate-900">{tx(item, lang)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
